import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  absoluteUrl,
  formatPageTitle,
  OG_IMAGE_URL,
  resolveCanonicalPath,
  ROBOTS_NOINDEX,
  SITE_NAME,
} from './seoConfig';

export type SeoHeadProps = {
  title: string;
  description: string;
  canonicalPath?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const SeoHead = ({
  title,
  description,
  canonicalPath = '/',
  noindex = ROBOTS_NOINDEX,
  jsonLd,
}: SeoHeadProps) => {
  const documentTitle = formatPageTitle(title);
  const canonical = absoluteUrl(resolveCanonicalPath(canonicalPath));
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet>
      <title>{documentTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={documentTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={OG_IMAGE_URL} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={documentTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
};

export default SeoHead;
